import { useMutation } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { useLocale } from '~/hooks/useLocale';
import { type ApiDateParams, timeframeToApiParams } from './studentDataParams';
import { createLogger } from '~/utils/logging';
import { sanitizeTagValue } from '~/utils/instrumentation';
import { ClientError } from '~/errors';

const log = createLogger('useStudentGenerateMessageMutation');

export type IncludeKey = 'praises' | 'warnings' | 'attendance';
export type Timeframe = 'today' | 'week' | 'month' | '3months' | 'all';
export type MessageFormat = 'email' | 'phone' | 'summary';

export type StudentGenerateMessageRequest = {
  studentId: string;
  periodId: string;
  periodCreatedAt: string;
  timeframe: Timeframe;
  include: IncludeKey[];
  format: MessageFormat;
};

export type MessageContext = {
  sessions: number;
  praises: number;
  warnings: number;
  absences: number;
};

type StudentGenerateMessageApiRequest = {
  studentId: string;
  periodId: string;
  tz: string;
  include: IncludeKey[];
  format: MessageFormat;
} & ApiDateParams;

type StudentGenerateMessageResponse = {
  message: string;
  context?: MessageContext;
};

export function useStudentGenerateMessageMutation() {
  const httpClient = useHttpClient();
  const locale = useLocale();

  const { data, error, mutate, reset, status } = useMutation({
    mutationFn: async (request: StudentGenerateMessageRequest) => {
      const tz = locale?.timeZone ?? '';
      const dateParams = timeframeToApiParams(request.timeframe, request.periodCreatedAt);

      log.debug('Generating student message', {
        studentId: request.studentId,
        periodId: request.periodId,
        timeframe: request.timeframe,
        format: request.format,
      });

      const apiRequest: StudentGenerateMessageApiRequest = {
        studentId: request.studentId,
        periodId: request.periodId,
        tz,
        include: request.include,
        format: request.format,
        ...dateParams,
      };

      const result = await httpClient.post<StudentGenerateMessageResponse>('/report/generateMessage', apiRequest);
      log.info('Generated student message successfully', {
        studentId: request.studentId,
        messageLength: result.message.length,
      });
      return result;
    },
    onError: (error: Error) => {
      if (error instanceof ClientError) {
        // Ignore client errors.
      } else {
        log.error('Failed to generate student message', {
          errorMessage: sanitizeTagValue(error.message),
        });
      }
    },
  });

  return {
    error,
    mutate,
    reset,
    status,
    message: data?.message,
    context: data?.context,
  };
}
